import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Link,
  Paper,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Fade,
} from '@mui/material';
import {
  MenuBook,
  CheckCircle,
  Star,
  AutoStories,
  EmojiEvents,
  Search,
  Add,
  ChatBubbleOutline,
  TrendingUp,
  ArrowForward,
  LocalLibrary,
  PlayArrow,
} from '@mui/icons-material';

function Home() {
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [currentlyReading, setCurrentlyReading] = useState([]);
  const [stats, setStats] = useState({
    totalBooks: 0,
    finishedBooks: 0,
    currentlyReading: 0,
    avgRating: 0,
  });
  const [challenges, setChallenges] = useState([]);
  const [activeStories, setActiveStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        // Fetch all data in parallel
        const [statsRes, currentlyReadingRes, featuredRes, challengesRes, storiesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/books/stats/summary`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/books/status/currently-reading`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/books/featured`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/challenges`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/stories`, {
            params: { userId: 'princess-nabilah' },
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })), // Gracefully handle if stories endpoint fails
        ]);

        setStats(statsRes.data);
        setCurrentlyReading(currentlyReadingRes.data);
        setFeaturedBooks(featuredRes.data);

        // Filter active challenges (not completed)
        const activeChallenges = challengesRes.data.filter(c => !c.completed);
        setChallenges(activeChallenges);

        // Filter active stories (not completed)
        const activeStoriesList = storiesRes.data.filter(s => !s.isCompleted);
        setActiveStories(activeStoriesList);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const StatCard = ({ icon, value, label, color }) => (
    <Fade in timeout={800}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          backgroundColor: '#F7EEDC',
          border: '1px solid rgba(141, 123, 104, 0.3)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 12px rgba(141, 123, 104, 0.2)',
            backgroundColor: '#F6EDD8',
          },
        }}
      >
        <Box sx={{ color: color || '#8D7B68', mb: 0.5 }}>
          {icon}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#555', fontWeight: 500, fontSize: '0.85rem' }}>
          {label}
        </Typography>
      </Paper>
    </Fade>
  );

  const QuickActionCard = ({ icon, title, path, color }) => (
    <Button
      component={RouterLink}
      to={path}
      variant="outlined"
      startIcon={icon}
      sx={{
        width: '100%',
        py: 1.5,
        px: 3,
        justifyContent: 'flex-start',
        textTransform: 'none',
        borderColor: '#8D7B68',
        color: '#333',
        borderRadius: 2,
        backgroundColor: '#fff',
        border: '2px solid #8D7B68',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#8D7B68',
          color: '#fff',
          borderColor: '#8D7B68',
          transform: 'translateX(4px)',
        },
        '& .MuiButton-startIcon': {
          color: color || '#8D7B68',
          transition: 'color 0.2s',
        },
        '&:hover .MuiButton-startIcon': {
          color: '#fff',
        },
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
        {title}
      </Typography>
    </Button>
  );

  return (
    <Box sx={{ backgroundColor: '#FAF3E0', minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 6 }}>

        {/* HERO SECTION */}
        <Fade in timeout={600}>
          <Box
            sx={{
              textAlign: 'center',
              mb: 5,
              py: 5,
              px: 3,
              background: 'linear-gradient(135deg, #FAF3E0 0%, #F4ECE3 100%)',
              borderRadius: 3,
              border: '2px solid #8D7B68',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <LocalLibrary sx={{ fontSize: 48, color: '#8D7B68', mb: 1.5 }} />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#333',
                mb: 1.5,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
              }}
            >
              The Library of Princess Nabilah
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#555',
                mb: 3,
                fontWeight: 400,
                fontSize: { xs: '0.95rem', sm: '1rem' },
              }}
            >
              Track, explore, and review your favorite books all in one place
            </Typography>
            <Button
              component={RouterLink}
              to="/search"
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                backgroundColor: '#8D7B68',
                color: '#fff',
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#7A6859',
                  transform: 'scale(1.03)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Start Reading Journey
            </Button>
          </Box>
        </Fade>

        {/* STATS DASHBOARD */}
        {!loading && (
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUp sx={{ color: '#8D7B68', mr: 1, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                Your Reading Journey
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<MenuBook sx={{ fontSize: 36 }} />}
                  value={stats.totalBooks}
                  label="Total Books"
                  color="#8D7B68"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<CheckCircle sx={{ fontSize: 36 }} />}
                  value={stats.finishedBooks}
                  label="Completed"
                  color="#4CAF50"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<Star sx={{ fontSize: 36 }} />}
                  value={stats.avgRating > 0 ? stats.avgRating : '-'}
                  label="Avg Rating"
                  color="#F4B400"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<AutoStories sx={{ fontSize: 36 }} />}
                  value={stats.currentlyReading}
                  label="Reading Now"
                  color="#8D7B68"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* CURRENTLY READING SECTION */}
        {currentlyReading.length > 0 && (
          <Fade in timeout={1000}>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AutoStories sx={{ color: '#8D7B68', mr: 1, fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                    Currently Reading
                  </Typography>
                </Box>
                <IconButton
                  component={RouterLink}
                  to="/my-books"
                  sx={{ color: '#8D7B68' }}
                >
                  <ArrowForward />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                {currentlyReading.map((book) => (
                  <Grid item xs={6} sm={4} md={2} key={book._id}>
                    <Link component={RouterLink} to={`/book/${book._id}`} underline="none">
                      <Card
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          backgroundColor: 'transparent',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid #8D7B68',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            backgroundColor: 'rgba(141, 123, 104, 0.05)',
                            borderColor: '#7A6859',
                            boxShadow: '0 4px 12px rgba(141, 123, 104, 0.2)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '2/3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={book.thumbnail || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="300"%3E%3Crect width="200" height="300" fill="%23F4ECE3"/%3E%3Ctext x="50%25" y="50%25" font-family="Georgia,serif" font-size="18" fill="%238D7B68" text-anchor="middle" dominant-baseline="middle"%3ENo Cover%3C/text%3E%3C/svg%3E'}
                            alt={book.title}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: '#333',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {book.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1 }}>
                            {book.authors?.join(', ')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        )}

        {/* FEATURED BOOKS SECTION */}
        {featuredBooks.length > 0 && (
          <Fade in timeout={1200}>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Star sx={{ color: '#F4B400', mr: 1, fontSize: 28 }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                    Featured in Your Library
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', mt: 0.5, fontSize: '0.85rem' }}>
                    Your highest-rated books
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2}>
                {featuredBooks.map((book) => (
                  <Grid item xs={6} sm={4} md={2} key={book._id}>
                    <Link component={RouterLink} to={`/book/${book._id}`} underline="none">
                      <Card
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          backgroundColor: 'transparent',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid #8D7B68',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            backgroundColor: 'rgba(141, 123, 104, 0.05)',
                            borderColor: '#7A6859',
                            boxShadow: '0 4px 12px rgba(141, 123, 104, 0.2)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '2/3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={book.thumbnail || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="300"%3E%3Crect width="200" height="300" fill="%23F4ECE3"/%3E%3Ctext x="50%25" y="50%25" font-family="Georgia,serif" font-size="18" fill="%238D7B68" text-anchor="middle" dominant-baseline="middle"%3ENo Cover%3C/text%3E%3C/svg%3E'}
                            alt={book.title}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: '#333',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.3,
                            }}
                          >
                            {book.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1 }}>
                            {book.authors?.join(', ')}
                          </Typography>
                          {book.rating > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  sx={{
                                    fontSize: 14,
                                    color: i < book.rating ? '#F4B400' : '#DDD',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        )}

        {/* READING CHALLENGES SECTION */}
        {challenges.length > 0 && (
          <Fade in timeout={1400}>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEvents sx={{ color: '#8D7B68', mr: 1, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                  Active Reading Challenges
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {challenges.slice(0, 2).map((challenge) => {
                  const progress = challenge.targetCount > 0
                    ? Math.round((challenge.currentProgress / challenge.targetCount) * 100)
                    : 0;
                  return (
                    <Grid item xs={12} md={6} key={challenge._id}>
                      <Box
                        sx={{
                          p: 2.5,
                          backgroundColor: 'rgba(141, 123, 104, 0.08)',
                          borderRadius: 2,
                          border: '1px solid #8D7B68',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(141, 123, 104, 0.12)',
                            boxShadow: '0 4px 12px rgba(141, 123, 104, 0.2)',
                          },
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                          Read {challenge.targetCount} {challenge.targetCount === 1 ? 'Book' : 'Books'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MenuBook sx={{ color: '#8D7B68', mr: 1, fontSize: 20 }} />
                          <Typography variant="body1" sx={{ color: '#555', fontWeight: 500 }}>
                            {challenge.currentProgress} / {challenge.targetCount} books
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#DDD',
                            mb: 1.5,
                            border: '1px solid #CCC',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#8D7B68',
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          🎯 Due: {new Date(challenge.targetDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/my-books"
                  variant="outlined"
                  sx={{
                    borderColor: '#8D7B68',
                    color: '#8D7B68',
                    px: 3,
                    py: 1,
                    borderRadius: 3,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#8D7B68',
                      color: '#fff',
                      borderColor: '#8D7B68',
                    },
                  }}
                >
                  View All Challenges
                </Button>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ROYAL TALES SECTION */}
        <Fade in timeout={1600}>
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AutoStories sx={{ color: '#8D7B68', mr: 1, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                  Royal Tales
                </Typography>
              </Box>
              <IconButton
                component={RouterLink}
                to="/stories"
                sx={{ color: '#8D7B68' }}
              >
                <ArrowForward />
              </IconButton>
            </Box>
            
            {activeStories.length > 0 ? (
              <Box
                sx={{
                  p: 4,
                  background: 'linear-gradient(135deg, #F7EEDC 0%, #F4ECE3 100%)',
                  border: '2px solid #8D7B68',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(141, 123, 104, 0.3)',
                  },
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ color: '#8D7B68', fontWeight: 700, mb: 1 }}>
                      Continue Your Adventure
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                      {activeStories[0].title}
                    </Typography>
                    <Chip
                      label={activeStories[0].genre}
                      size="small"
                      sx={{
                        backgroundColor: '#8D7B68',
                        color: '#FFF',
                        fontWeight: 600,
                        mb: 2,
                      }}
                    />
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          Chapter {activeStories[0].currentScene} of ~22
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#555', fontWeight: 600 }}>
                          {Math.round((activeStories[0].currentScene / 22) * 100)}% Complete
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          backgroundColor: '#DDD',
                          borderRadius: 4,
                          overflow: 'hidden',
                          border: '1px solid #8D7B68',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(activeStories[0].currentScene / 22) * 100}%`,
                            height: '100%',
                            backgroundColor: '#8D7B68',
                          }}
                        />
                      </Box>
                    </Box>
                    <Button
                      component={RouterLink}
                      to={`/story/${activeStories[0]._id}`}
                      variant="contained"
                      startIcon={<PlayArrow />}
                      sx={{
                        backgroundColor: '#8D7B68',
                        color: '#FFF',
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#7A6859',
                        },
                      }}
                    >
                      Continue Tale
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Box
                      component="img"
                      src={activeStories[0].scenes[0]?.imageUrl || ''}
                      alt="Story preview"
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '2px solid #8D7B68',
                      }}
                    />
                  </Grid>
                </Grid>
                {activeStories.length > 1 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#8D7B68',
                      fontWeight: 600,
                      mt: 2,
                      textAlign: 'center',
                    }}
                  >
                    + {activeStories.length - 1} more active {activeStories.length === 2 ? 'tale' : 'tales'}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 5,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #F7EEDC 0%, #F4ECE3 100%)',
                  border: '2px solid #8D7B68',
                  borderRadius: 3,
                }}
              >
                <AutoStories sx={{ fontSize: 64, color: '#8D7B6860', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#8D7B68', fontWeight: 700, mb: 1 }}>
                  Embark on a Magnificent Adventure
                </Typography>
                <Typography variant="body1" sx={{ color: '#555', mb: 3, maxWidth: 500, mx: 'auto' }}>
                  Choose your own path in enchanting tales crafted especially for Your Majesty. Each decision shapes your destiny!
                </Typography>
                <Button
                  component={RouterLink}
                  to="/story/new"
                  variant="contained"
                  endIcon={<ArrowForward />}
                  sx={{
                    backgroundColor: '#8D7B68',
                    color: '#FFF',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#7A6859',
                      transform: 'scale(1.03)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Begin Your First Tale
                </Button>
              </Box>
            )}
          </Box>
        </Fade>

        {/* QUICK ACTIONS SECTION */}
        <Fade in timeout={1800}>
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: '#333', mb: 2 }}
            >
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <QuickActionCard
                  icon={<MenuBook sx={{ fontSize: 24 }} />}
                  title="My Books"
                  path="/my-books"
                  color="#8D7B68"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <QuickActionCard
                  icon={<Search sx={{ fontSize: 24 }} />}
                  title="Search Books"
                  path="/search"
                  color="#8D7B68"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <QuickActionCard
                  icon={<Add sx={{ fontSize: 24 }} />}
                  title="Add Book"
                  path="/search"
                  color="#8D7B68"
                />
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* FOOTER */}
        <Divider sx={{ my: 4, borderColor: '#8D7B68', opacity: 0.3 }} />
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" sx={{ color: '#555' }}>
            © {new Date().getFullYear()} The Library of Princess Nabilah. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Home;
